/**
 * Updates an existing item in an array or inserts a new item if not found.
 *
 * The function is immutable (doesn't modify the original array) and returns
 * a new array with the changes applied.
 *
 * @param array - The source array to modify
 * @param item - The new item to insert or update with
 * @param matcherCallback - A callback function that returns true when the target item is found
 * @returns A new array with the item either updated or inserted
 *
 * @example
 * const users = [{id: 1, name: 'Alice'}, {id: 2, name: 'Bob'}];
 * const updatedUsers = upsertItem(
 *   users,
 *   {id: 2, name: 'Bobby'},
 *   (user) => user.id === 2
 * );
 */
export function upsertItem<T>(array: T[], item: T, matcherCallback: (item: T) => boolean): T[] {
  // Find the position of the existing item (if any) using the provided search function
  const existingIndex = array.findIndex(matcherCallback);

  // If item wasn't found (-1), add the new item to the end of a new array
  if (existingIndex === -1) {
    return [...array, item];
  }

  // If item was found, create new array with the item replaced at the found position
  return array.map((existing, index) => (index === existingIndex ? item : existing));
}
